import { Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import React, { useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const SAMPLE_HEADERS = ["Name", "Symbol"];
const SAMPLE_ROWS = [
  ["US Dollar", "USD"],
  ["Indian Rupee", "INR"],
  ["Euro", "EUR"],
];

const downloadSampleFormat = () => {
  const ws = XLSX.utils.aoa_to_sheet([SAMPLE_HEADERS, ...SAMPLE_ROWS]);
  const colWidths = [{ wch: 16 }, { wch: 10 }];
  ws["!cols"] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Currencies");
  XLSX.writeFile(wb, "product_currency_import_format.xlsx");
};

const normalizeKey = (key) => {
  if (!key || typeof key !== "string") return "";
  const k = key.toString().trim().toLowerCase().replace(/\s+/g, "_");
  const map = {
    name: "name",
    symbol: "symbol",
  };
  return map[k] || key.trim();
};

const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];
  const headerLine = lines[0];
  const headers = headerLine.split(",").map((h) => normalizeKey(h.trim()));
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(",").map((v) => v.trim());
    const obj = {};
    headers.forEach((h, idx) => {
      if (h) obj[h] = values[idx] != null ? values[idx] : "";
    });
    if (obj.name || obj.symbol) rows.push(obj);
  }
  return rows;
};

const parseExcel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheet];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        if (!json.length) {
          resolve([]);
          return;
        }
        const rawHeaders = json[0].map((h) => (h != null ? String(h) : ""));
        const headers = rawHeaders.map((h) => normalizeKey(h));
        const rows = [];
        for (let i = 1; i < json.length; i++) {
          const row = json[i];
          const obj = {};
          headers.forEach((h, idx) => {
            if (h) obj[h] = row[idx] != null ? String(row[idx]).trim() : "";
          });
          if (obj.name || obj.symbol) rows.push(obj);
        }
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
};

const UploadProductCurrencyExcel = ({ onSuccess }) => {
  const [loader, setLoader] = useState(false);
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      message.warning("Please select a CSV or Excel file.");
      return;
    }
    const fileName = (file.name || "").toLowerCase();
    const isCsv = fileName.endsWith(".csv");
    const isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");
    if (!isCsv && !isExcel) {
      message.warning("Please upload a .csv, .xlsx, or .xls file.");
      return;
    }

    setLoader(true);
    try {
      let rows = [];
      if (isCsv) {
        const text = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsText(file);
        });
        rows = parseCSV(text);
      } else {
        rows = await parseExcel(file);
      }

      const payload = rows
        .map((row) => ({
          name: String(row.name || "").trim(),
          symbol: String(row.symbol || "").trim(),
        }))
        .filter((r) => r.name && r.symbol);

      if (payload.length === 0) {
        message.warning("No valid rows. Each row needs Name and Symbol.");
        setLoader(false);
        return;
      }

      const { data } = await axios.post("product-currency?query=createmany", payload);
      setLoader(false);
      setFile(null);
      e.target.reset();

      const count = data?.count ?? payload.length;
      toast.success(`Imported ${count} currency(ies) successfully.`);
      if (onSuccess) onSuccess();
    } catch (err) {
      setLoader(false);
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || "Import failed.";
      toast.error(msg);
    }
  };

  return (
    <div className="text-center mt-2">
      <Button
        type="default"
        icon={<DownloadOutlined />}
        onClick={downloadSampleFormat}
        className="mb-2"
      >
        Download sample format (.xlsx)
      </Button>
      <form onSubmit={handleSubmit}>
        <input
          className="form-control"
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
        />
        <br />
        <Button className="mt-2" type="primary" htmlType="submit" shape="round" loading={loader}>
          Import from Excel
        </Button>
      </form>
    </div>
  );
};

export default UploadProductCurrencyExcel;
